import { useMemo } from "react";
import { styled } from "styled-components";

import { Button, DesktopAndTablet, Divider, Text } from "@vericus/cadmus-ui";

import feedbackIllustration from "@/assets/illustrations/student-pointing-submission-paper.png";
import { ProvisionalFeedbackPill } from "@/features/feedback/components/provisional-feedback-pill";
import { FeedbackTabId } from "@/features/feedback/components/types";
import { formatDecimalNumber } from "@/features/feedback/utils";
import { useMarkingToolLaunch } from "@/features/submission";
import {
  FeedbackLevel,
  MarkingTool,
  ResultFragment,
  StudentOutcomeScoreFragment,
} from "@/generated/graphql";

import {
  ActionContainer,
  ContentContainer,
  Illustration,
  InfoContainer,
} from "./styles";

interface Props {
  assessmentName: string;
  userName: string;
  latestResult: ResultFragment;
  markingTool: MarkingTool;
  studentOutcomeScore: StudentOutcomeScoreFragment;
  finalFeedbackLevel: FeedbackLevel | null;
  provisionalFeedbackLevel: FeedbackLevel | null;
}

/**
 * ExamFeedbackConfirmation displays the feedback and grades post-exam.
 */
export function ExamFeedbackConfirmation(props: Props) {
  const {
    assessmentName,
    userName,
    latestResult,
    markingTool,
    studentOutcomeScore,
  } = props;

  const isReady = useMemo(() => {
    if (markingTool === MarkingTool.Speedgrader) {
      return latestResult.canvasResultUrl !== null;
    } else if (markingTool === MarkingTool.Moodlegrader) {
      return latestResult.moodleGraderUrl !== null;
    } else if (markingTool === MarkingTool.Cadmusgrader) {
      // parent component checks studentOutcomeScore
      // to determine feedbackIsAvailable
      return true;
    } else if (markingTool === MarkingTool.Turnitin) {
      return latestResult.heraResultId !== null;
    }
    return latestResult.heraResultId !== null;
  }, [latestResult, markingTool]);

  const onViewFeedback = useMarkingToolLaunch({
    workId: latestResult.workId,
    markingTool,
    submissionType: latestResult.submissionType,
    isReady,
    component: FeedbackTabId.IndividualReport,
  });

  // Prioritize the data of a final release so that if there is a provisional
  // and final feedback release, the grade shows the final.
  const gradeData =
    studentOutcomeScore.final.total.score !== null &&
    props.finalFeedbackLevel !== null &&
    [FeedbackLevel.Mark, FeedbackLevel.Answer].includes(
      props.finalFeedbackLevel
    )
      ? `${formatDecimalNumber(studentOutcomeScore.final.total.score)} / ${studentOutcomeScore.final.total.maxScore}`
      : studentOutcomeScore.provisional.total.score &&
          props.provisionalFeedbackLevel !== null &&
          [FeedbackLevel.Mark, FeedbackLevel.Answer].includes(
            props.provisionalFeedbackLevel
          )
        ? `${formatDecimalNumber(studentOutcomeScore.provisional.total.score)} / ${studentOutcomeScore.provisional.total.maxScore}`
        : "—";

  const isProvisional =
    studentOutcomeScore.final.total.score === null &&
    typeof studentOutcomeScore.provisional.total.score == "number";

  return (
    <Container>
      <Text kind="displayTwo">Your feedback is available now</Text>
      <Divider />
      <ContentContainer>
        <InfoContainer>
          <div>
            <Text kind="headingThree">{assessmentName}</Text>
            <Text kind="label">Submitted by {userName}</Text>
          </div>
          <ActionContainer>
            <div>
              {isProvisional && (
                <div
                  style={{
                    marginBottom: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ProvisionalFeedbackPill label={"Provisional Feedback"} />
                </div>
              )}
              <Text kind="label" textAlign="center">
                Grade
              </Text>
              <Text kind="headingFour" textAlign="center">
                {gradeData}
              </Text>
            </div>

            <Button
              kind="secondary"
              fullWidth
              disabled={!onViewFeedback}
              title={
                !onViewFeedback
                  ? "Feedback is not available right now"
                  : undefined
              }
              onClick={onViewFeedback}
            >
              View feedback
            </Button>
          </ActionContainer>
        </InfoContainer>
        <DesktopAndTablet>
          <Illustration src={feedbackIllustration} alt="" />
        </DesktopAndTablet>
      </ContentContainer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;
